/**
 * Shared API error types used across the bookaclassic API clients
 * (`users-api.ts`, `bookings-api.ts`, …).
 *
 * `RateLimitError` is the canonical handled rate-limit (HTTP 429)
 * error. It is thrown by every `authFetch` when a request is rate
 * limited, carries the parsed `retryAfter` (seconds) so call sites can
 * surface a friendly "please wait ~N seconds" message, and is
 * suppressed in Sentry's `beforeSend` (lib/sentry.ts) because it is a
 * handled, non-fatal condition (a hammered user), not a bug.
 */
export class RateLimitError extends Error {
  retryAfter: number;
  constructor(message: string, retryAfter: number) {
    super(message);
    this.name = "RateLimitError";
    this.retryAfter = retryAfter;
  }
}

/**
 * Type guard for the shared rate-limit (HTTP 429) error. Matches both a
 * real `RateLimitError` instance and any `Error` whose `name` is
 * `"RateLimitError"` (covers cross-module / re-thrown copies where
 * `instanceof` can fail), so call sites can branch on a 429 with a
 * single, dependable check.
 */
export function isRateLimitError(error: unknown): error is RateLimitError {
  return (
    error instanceof RateLimitError ||
    (error instanceof Error && error.name === "RateLimitError")
  );
}

/**
 * True when an error is the "this booking can't be messaged" 404 that
 * POST /messages/conversations/find-or-create returns for a trip whose
 * participants (car_id / renter_id / owner_id) are NULL — legacy imported
 * bookings being the common case. The server deliberately returns a 404
 * (`{ error: "Trip not found or participants unavailable" }`, or the earlier
 * `"trip not found"` pre-check) rather than a 500, so the client can surface
 * a contextual explanation instead of a generic failure or a silent swallow
 * (task #5759). Matched on status + message so unrelated 404s (e.g. the
 * enquiry-slot "Listing not found") fall through to the caller's handling.
 */
export function isConversationUnavailableError(error: unknown): boolean {
  const e = error as { status?: number; message?: string } | undefined;
  if (!e || e.status !== 404) return false;
  const msg = (e.message ?? "").trim().toLowerCase();
  return (
    msg === "trip not found or participants unavailable" ||
    msg === "trip not found"
  );
}

/**
 * Parse the wait time (seconds) for a 429 response, preferring the
 * `retryAfter` body field, then the `Retry-After` header, then a
 * sensible ~60s fallback.
 */
export function parseRetryAfter(res: Response, body: { retryAfter?: unknown }): number {
  const headerRetry = Number(res.headers.get("Retry-After") || 0);
  return Number(body?.retryAfter) || headerRetry || 60;
}

/**
 * Build the friendly, seconds-aware rate-limit message, preferring the
 * server's own `error` copy when present.
 */
export function buildRateLimitMessage(body: { error?: unknown }, retryAfter: number): string {
  return typeof body?.error === "string" && body.error.length > 0
    ? body.error
    : `Too many attempts. Please wait ~${retryAfter} second${retryAfter === 1 ? "" : "s"} and try again.`;
}
